import { useTranslation } from 'react-i18next';
import PublicContentPage, { PublicCtaLink } from '../components/layout/PublicContentPage';
import { buildArticleSchema } from '../lib/seo';

const GUIDE_PATH = '/can-ai-detect-accessibility-issues';
const GUIDE_DATE = '2026-05-21';

interface GuideSection {
  title: string;
  body: string;
  points?: string[];
}

interface GuideStep {
  title: string;
  body: string;
}

interface GuideAction {
  href: string;
  label: string;
  variant: 'primary' | 'secondary' | 'ghost';
}

export default function AiAccessibilityGuidePage() {
  const { t } = useTranslation('marketing');
  const capabilities = t('pages.aiAccessibilityGuide.capabilities.items', { returnObjects: true }) as string[];
  const workflowSteps = t('pages.aiAccessibilityGuide.workflow.steps', { returnObjects: true }) as GuideStep[];
  const sections = t('pages.aiAccessibilityGuide.sections', { returnObjects: true }) as GuideSection[];
  const sidebarPoints = t('pages.aiAccessibilityGuide.sidebar.points', { returnObjects: true }) as string[];
  const ctaActions = t('pages.aiAccessibilityGuide.cta.actions', { returnObjects: true }) as GuideAction[];

  return (
    <PublicContentPage
      metaTitle={t('pages.aiAccessibilityGuide.meta.title')}
      metaDescription={t('pages.aiAccessibilityGuide.meta.description')}
      canonicalPath={GUIDE_PATH}
      eyebrow={t('pages.aiAccessibilityGuide.hero.eyebrow')}
      title={t('pages.aiAccessibilityGuide.hero.title')}
      intro={t('pages.aiAccessibilityGuide.hero.intro')}
      metaType="article"
      structuredData={[
        buildArticleSchema({
          headline: t('pages.aiAccessibilityGuide.hero.title'),
          description: t('pages.aiAccessibilityGuide.meta.description'),
          path: GUIDE_PATH,
          datePublished: GUIDE_DATE,
          dateModified: GUIDE_DATE,
        }),
      ]}
      aside={(
        <>
          <section className="acc-public-page-card acc-public-page-card--sticky">
            <h2>{t('pages.aiAccessibilityGuide.sidebar.title')}</h2>
            <p>{t('pages.aiAccessibilityGuide.sidebar.body')}</p>
            <ul className="acc-public-page-list">
              {sidebarPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>

          <section className="acc-public-page-card">
            <h2>{t('pages.aiAccessibilityGuide.sidebar.noteTitle')}</h2>
            <p>{t('pages.aiAccessibilityGuide.sidebar.noteBody')}</p>
          </section>
        </>
      )}
    >
      <section className="acc-public-page-card">
        <h2>{t('pages.aiAccessibilityGuide.shortAnswer.title')}</h2>
        <p>{t('pages.aiAccessibilityGuide.shortAnswer.body')}</p>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.aiAccessibilityGuide.capabilities.title')}</h2>
        <p>{t('pages.aiAccessibilityGuide.capabilities.body')}</p>
        <ul className="acc-public-page-list">
          {capabilities.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      {sections.map((section) => (
        <section key={section.title} className="acc-public-page-card">
          <h2>{section.title}</h2>
          <p>{section.body}</p>
          {section.points && section.points.length > 0 && (
            <ul className="acc-public-page-list">
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <section className="acc-public-page-section" aria-labelledby="ai-workflow-title">
        <div>
          <h2 id="ai-workflow-title">{t('pages.aiAccessibilityGuide.workflow.title')}</h2>
          <p>{t('pages.aiAccessibilityGuide.workflow.body')}</p>
        </div>

        <div className="acc-public-page-grid">
          {workflowSteps.map((step, index) => (
            <article key={step.title} className="acc-public-page-subcard">
              <h3>
                {t('pages.aiAccessibilityGuide.workflow.stepLabel', { number: index + 1 })}
                {' '}
                {step.title}
              </h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.aiAccessibilityGuide.disclaimer.title')}</h2>
        <p>{t('pages.aiAccessibilityGuide.disclaimer.body')}</p>
      </section>

      <section className="acc-public-cta-band" aria-labelledby="ai-guide-cta-title">
        <div>
          <h2 id="ai-guide-cta-title">{t('pages.aiAccessibilityGuide.cta.title')}</h2>
          <p>{t('pages.aiAccessibilityGuide.cta.body')}</p>
        </div>
        <div className="acc-public-cta-actions">
          {ctaActions.map((action) => (
            <PublicCtaLink key={action.href} href={action.href} variant={action.variant}>
              {action.label}
            </PublicCtaLink>
          ))}
        </div>
      </section>
    </PublicContentPage>
  );
}
